import React, { useCallback, useEffect, useMemo, useState } from 'react'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT } from 'material-react-table/locales/pt'
import {
  Badge,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Label,
} from 'reactstrap'
import moment from 'moment'
import { mmYyyyFromMesRef } from '../../../utils/monthSelectOptions'
import { fetchRhDetalhamento } from './rhDashboardApi'
import RhDateRangeCalendar from './RhDateRangeCalendar'

const mrtCompactProps = {
  muiTableBodyCellProps: { sx: { fontSize: '11px' } },
  muiTableHeadCellProps: {
    sx: {
      '.MuiSvgIcon-root': { fontSize: '18px' },
      fontSize: '12px',
      fontWeight: 'normal',
      padding: '8px',
    },
  },
  muiTablePaginationProps: {
    sx: {
      '.MuiTablePagination-displayedRows': { fontSize: '12px' },
      marginTop: '0px',
      fontSize: '12px',
    },
    labelRowsPerPage: <span style={{ fontSize: '12px' }}>Linhas por página</span>,
    rowsPerPageOptions: [10, 31, 50],
  },
  enableDensityToggle: false,
  enableColumnFilters: false,
  positionPagination: 'bottom',
}

const selectStyle = {
  width: '100%',
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #ced4da',
  fontSize: '0.85rem',
}

const RhDetalhamentoPanel = ({
  mesRef,
  mesRange,
  colaboradores,
  selectedPfId,
  onPfIdChange,
  onOpenExtrato,
}) => {
  const [dataIni, setDataIni] = useState(mesRange.start)
  const [dataFim, setDataFim] = useState(mesRange.max)
  const [projetoFilter, setProjetoFilter] = useState('')
  const [detalhe, setDetalhe] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    setDataIni(mesRange.start)
    setDataFim(mesRange.max)
    setProjetoFilter('')
  }, [mesRef, mesRange.start, mesRange.max])

  const mesApi = useMemo(() => {
    if (dataIni) return moment(dataIni, 'YYYY-MM-DD').format('MM/YYYY')
    return mmYyyyFromMesRef(mesRef)
  }, [dataIni, mesRef])

  const loadDetalhamento = useCallback(async () => {
    if (!dataIni || !dataFim) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetchRhDetalhamento({
        mesMmYyyy: mesApi,
        dataIni,
        dataFim,
        pfId: selectedPfId || undefined,
        projeto: projetoFilter || undefined,
      })
      if (res?.ok === false) {
        setDetalhe(null)
        setError(res.msg || 'Erro ao carregar detalhamento.')
      } else {
        setDetalhe(res)
      }
    } catch (err) {
      setDetalhe(null)
      setError(String(err))
    } finally {
      setLoading(false)
    }
  }, [mesApi, dataIni, dataFim, selectedPfId, projetoFilter])

  useEffect(() => {
    loadDetalhamento()
  }, [loadDetalhamento])

  const projetoOptions = detalhe?.projetos || []
  const dias = detalhe?.dias || []
  const totais = detalhe?.totais || {}
  const showColaboradorCol = !selectedPfId

  const columns = useMemo(() => {
    const cols = [
      { accessorKey: 'data_fmt', header: 'Dia', size: 100 },
    ]
    if (showColaboradorCol) {
      cols.push({ accessorKey: 'colaborador', header: 'Colaborador', size: 140 })
    }
    cols.push(
      {
        accessorKey: 'entrada_fmt',
        header: 'Entrada',
        Cell: ({ cell }) => cell.getValue() || '—',
      },
      {
        accessorKey: 'saida_fmt',
        header: 'Saída',
        Cell: ({ cell }) => cell.getValue() || '—',
      },
      { accessorKey: 'tempo_dia_fmt', header: 'Tempo', size: 90 },
      {
        id: 'retroativos',
        header: 'Retroativos',
        size: 90,
        Cell: ({ row }) => {
          const qtd = row.original.retroativos || 0
          return qtd > 0 ? <Badge color="warning" pill>{qtd}</Badge> : '—'
        },
      },
      {
        id: 'qtd_ativ',
        header: 'Atividades',
        size: 90,
        Cell: ({ row }) => (row.original.atividades?.length || 0),
      },
    )
    return cols
  }, [showColaboradorCol])

  const renderDetailPanel = ({ row }) => {
    const atividades = row.original.atividades || []
    if (!atividades.length) {
      return <div className="p-3 text-muted small">Nenhuma atividade neste dia.</div>
    }
    return (
      <div className="p-2" style={{ background: '#fafbfc' }}>
        <MaterialReactTable
          columns={[
            {
              accessorKey: 'projeto_nome',
              header: 'Projeto / atividade',
              Cell: ({ row: r }) => (
                <div>
                  <div>{r.original.pontoAtiv_desc || r.original.projeto_nome || '—'}</div>
                  {r.original.is_retroativo && (
                    <Badge color="warning" className="mt-1" pill style={{ fontSize: '0.65rem' }}>Retroativo</Badge>
                  )}
                </div>
              ),
            },
            { accessorKey: 'colaborador', header: 'Colaborador', size: 120 },
            { accessorKey: 'pontoAtiv_tempo', header: 'Tempo', size: 80 },
            {
              accessorKey: 'pontoAtiv_justif',
              header: 'Justificativa',
              Cell: ({ cell }) => cell.getValue() || '—',
            },
          ]}
          data={atividades}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enablePagination={false}
          enableColumnActions={false}
          enableSorting={false}
          localization={MRT_Localization_PT}
          {...mrtCompactProps}
          muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
        />
      </div>
    )
  }

  const selectedColaborador = colaboradores.find(c => String(c.pf_id) === String(selectedPfId))

  const handlePeriodoChange = ({ start, end }) => {
    if (start) setDataIni(start)
    if (end) setDataFim(end)
  }

  const limparFiltros = () => {
    setDataIni(mesRange.start)
    setDataFim(mesRange.max)
    setProjetoFilter('')
    onPfIdChange('')
  }

  return (
    <Row>
      <Col lg={3} md={4} className="mb-3 mb-md-0">
        <Card className="h-100 border">
          <CardBody>
            <h6 className="mb-3">Filtros</h6>

            <FormGroup>
              <Label className="small text-muted mb-2">Período</Label>
              <RhDateRangeCalendar
                min={mesRange.min}
                max={mesRange.max}
                startDate={dataIni}
                endDate={dataFim}
                onChange={handlePeriodoChange}
              />
              <div className="text-muted small mt-2">
                {dataIni && dataFim ? `${dataIni} → ${dataFim}` : 'Selecione data de início e fim'}
                {mesApi !== mmYyyyFromMesRef(mesRef) && (
                  <span className="d-block mt-1">
                    Período em
                    {' '}
                    {mesApi}
                  </span>
                )}
              </div>
            </FormGroup>

            <FormGroup>
              <Label className="small text-muted">Colaborador</Label>
              <select
                value={selectedPfId || ''}
                onChange={e => onPfIdChange(e.target.value)}
                style={selectStyle}
              >
                <option value="">Todos com contrato no mês</option>
                {colaboradores.map(c => (
                  <option key={c.pf_id} value={c.pf_id}>{c.pf_nomeExibicao}</option>
                ))}
              </select>
            </FormGroup>

            <FormGroup>
              <Label className="small text-muted">Projeto</Label>
              <select
                value={projetoFilter}
                onChange={e => setProjetoFilter(e.target.value)}
                style={selectStyle}
              >
                <option value="">Todos</option>
                {projetoOptions.map(nome => (
                  <option key={nome} value={nome}>{nome}</option>
                ))}
              </select>
            </FormGroup>

            <Button size="sm" color="light" block onClick={limparFiltros}>
              Limpar filtros
            </Button>
          </CardBody>
        </Card>
      </Col>

      <Col lg={9} md={8}>
        <Card className="border h-100">
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-3" style={{ gap: 8 }}>
              <div>
                <div className="text-muted small">Resultado do período</div>
                <div className="font-weight-bold">
                  {totais.dias || 0}
                  {' '}
                  dia(s) ·
                  {' '}
                  {totais.atividades || 0}
                  {' '}
                  atividade(s) ·
                  {' '}
                  {totais.tempo_fmt || '0h00min'}
                </div>
              </div>
              <div className="d-flex" style={{ gap: 6 }}>
                {selectedColaborador && typeof onOpenExtrato === 'function' && (
                  <Button size="sm" color="primary" outline onClick={() => onOpenExtrato(selectedColaborador)}>
                    <i className="ri-file-list-3-line mr-1" />
                    Extrato e gestão
                  </Button>
                )}
                <Button size="sm" color="light" onClick={loadDetalhamento} disabled={loading}>
                  Atualizar
                </Button>
              </div>
            </div>

            {loading && <div className="text-center py-4"><Spinner color="primary" /></div>}
            {!loading && error && <Alert color="danger" className="mb-0">{error}</Alert>}
            {!loading && !error && dias.length === 0 && (
              <p className="text-muted mb-0">Nenhuma atividade encontrada para os filtros selecionados.</p>
            )}
            {!loading && !error && dias.length > 0 && (
              <MaterialReactTable
                columns={columns}
                data={dias}
                getRowId={row => `${row.data}_${row.pf_id}`}
                initialState={{
                  density: 'compact',
                  pagination: { pageIndex: 0, pageSize: 31 },
                  sorting: [{ id: 'data_fmt', desc: true }],
                }}
                enableExpanding
                enableExpandAll={false}
                renderDetailPanel={renderDetailPanel}
                enableColumnResizing
                enableGlobalFilter
                enablePagination={dias.length > 10}
                localization={MRT_Localization_PT}
                {...mrtCompactProps}
                muiTablePaperProps={{ sx: { boxShadow: 'none', border: '1px solid #eef0f3' } }}
              />
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

export default RhDetalhamentoPanel
